import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { PhoneCall, PhoneOff, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { fetchLiveCalls, stopLiveCall } from "../../services/crmServices/campaignService";

// Uses the server-computed elapsedSeconds as the baseline (immune to any clock
// skew between the server and this browser) and ticks up locally each second.
// Re-anchors whenever a fresh value arrives from the 5s poll.
function LiveDuration({ elapsedSeconds, startedAt }) {
    const initial =
        typeof elapsedSeconds === "number"
            ? elapsedSeconds
            : startedAt
            ? Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000))
            : null;
    const [seconds, setSeconds] = useState(initial ?? 0);
    const anchorRef = useRef({ base: initial ?? 0, at: Date.now() });

    useEffect(() => {
        anchorRef.current = { base: initial ?? 0, at: Date.now() };
        setSeconds(initial ?? 0);
    }, [elapsedSeconds, startedAt]); // eslint-disable-line

    useEffect(() => {
        const timer = setInterval(() => {
            const { base, at } = anchorRef.current;
            setSeconds(base + Math.floor((Date.now() - at) / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    if (initial === null) return <span>—</span>;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return <span className="font-mono">{m}:{String(s).padStart(2, "0")}</span>;
}

export function LiveCallsPanel() {
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stoppingId, setStoppingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await fetchLiveCalls();
            setCalls(res?.data || []);
        } catch (err) {
            console.error("Failed to load live calls", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, 5000);
        return () => clearInterval(timer);
    }, [load]);

    const handleStop = async (call) => {
        if (!window.confirm("Hang up this call?")) return;
        setStoppingId(call._id);
        try {
            await stopLiveCall(call._id);
            toast.success("Call stopped");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to stop call");
        } finally {
            setStoppingId(null);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <PhoneCall className="h-4 w-4 text-green-600" />
                        {calls.length > 0 && (
                            <span className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                        )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">
                        Live Calls {calls.length > 0 && <span className="text-green-600">({calls.length} in progress)</span>}
                    </h3>
                </div>
                <Button variant="outline" size="sm" onClick={load}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
            ) : calls.length === 0 ? (
                <div className="py-12 text-center">
                    <p className="text-sm text-gray-400">No calls in progress</p>
                    <p className="text-xs text-gray-400 mt-1">This panel refreshes every 5 seconds</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50/80">
                            <tr>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Lead</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Campaign</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
                                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {calls.map((call) => (
                                <tr key={call._id} className="hover:bg-gray-50/50">
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse shrink-0" />
                                            <span className="text-sm text-gray-900">
                                                {call.lead ? `${call.lead.firstName || ""} ${call.lead.lastName || ""}`.trim() || "—" : "—"}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-gray-600 font-mono">{call.toNumber}</td>
                                    <td className="px-4 py-2.5">
                                        <span className="text-sm text-gray-600">{call.campaign?.name || "—"}</span>
                                        {call.campaign?.isTest && <Badge variant="outline" className="ml-1.5 text-[10px]">test</Badge>}
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-gray-900">
                                        <LiveDuration elapsedSeconds={call.elapsedSeconds} startedAt={call.startedAt} />
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={stoppingId === call._id}
                                            onClick={() => handleStop(call)}
                                            className="text-red-600 border-red-200 hover:bg-red-50"
                                        >
                                            {stoppingId === call._id
                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                : <><PhoneOff className="h-3.5 w-3.5 mr-1" /> Hang up</>}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
